'use client';

/**
 * Sandbox authentication using OAuth2 Implicit Flow.
 * This redirects users to Sandbox's unified auth page.
 */

const KEYCLOAK_URL = process.env.NEXT_PUBLIC_KEYCLOAK_URL!;
const KEYCLOAK_REALM = process.env.NEXT_PUBLIC_KEYCLOAK_REALM!;
const KEYCLOAK_CLIENT_ID = process.env.NEXT_PUBLIC_KEYCLOAK_CLIENT_ID!;

/**
 * Generate a random state parameter for CSRF protection
 */
function generateState(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Generate a random nonce parameter
 */
function generateNonce(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Get the URL to initiate Sandbox authentication using implicit flow.
 * Tokens are returned in the URL fragment (#) after authentication.
 */
export function getSandboxLoginUrl(redirectPath: string = '/dashboard'): string {
  const authUrl = `${KEYCLOAK_URL}/realms/${encodeURIComponent(KEYCLOAK_REALM)}/protocol/openid-connect/auth`;

  const state = generateState();
  const nonce = generateNonce();

  // Store state and nonce for validation
  sessionStorage.setItem('oauth_state', state);
  sessionStorage.setItem('oauth_nonce', nonce);
  sessionStorage.setItem('oauth_redirect_path', redirectPath);

  // Detect if running in Electron
  const isElectron = typeof window !== 'undefined' && (window as any).electronAPI !== undefined;

  const deepLinkProtocol = process.env.NEXT_PUBLIC_DEEP_LINK_PROTOCOL || 'genesys-dev';

  // Use custom protocol for Electron, HTTP for web
  const redirectUri = isElectron
    ? `${deepLinkProtocol}://auth/callback` // Deep link for Electron
    : `${window.location.origin}/auth/sandbox-callback`; // HTTP for web

  const params = new URLSearchParams({
    client_id: KEYCLOAK_CLIENT_ID,
    redirect_uri: redirectUri,
    state: state,
    response_mode: 'fragment',
    response_type: 'id_token token',
    scope: 'openid email profile roles',
    nonce: nonce,
  });

  return `${authUrl}?${params.toString()}`;
}

/**
 * Parse tokens from URL fragment (hash) after implicit flow redirect
 */
export function parseTokensFromFragment(): {
  accessToken: string | null;
  idToken: string | null;
  state: string | null;
  error: string | null;
  } {
  const hash = window.location.hash.substring(1); // Remove '#'
  const params = new URLSearchParams(hash);

  return {
    accessToken: params.get('access_token'),
    idToken: params.get('id_token'),
    state: params.get('state'),
    error: params.get('error'),
  };
}

/**
 * Validate state parameter to prevent CSRF attacks
 */
export function validateState(receivedState: string): boolean {
  const storedState = sessionStorage.getItem('oauth_state');
  sessionStorage.removeItem('oauth_state');
  return storedState === receivedState;
}

/**
 * Get the stored redirect path
 */
export function getStoredRedirectPath(): string {
  const path = sessionStorage.getItem('oauth_redirect_path') || '/dashboard';
  sessionStorage.removeItem('oauth_redirect_path');
  sessionStorage.removeItem('oauth_nonce'); // Clean up nonce too
  return path;
}

/**
 * Get the logout URL for Sandbox
 */
export function getSandboxLogoutUrl(redirectUri: string, idToken?: string): string {
  const logoutUrl = `${KEYCLOAK_URL}/realms/${encodeURIComponent(KEYCLOAK_REALM)}/protocol/openid-connect/logout`;

  const params = new URLSearchParams({
    post_logout_redirect_uri: redirectUri,
    client_id: KEYCLOAK_CLIENT_ID,
  });

  if (idToken) {
    params.set('id_token_hint', idToken);
  }

  return `${logoutUrl}?${params.toString()}`;
}
